import { useRef, useState } from "react";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

export default function InquiryForm({ cake = null, compact = false }) {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        event_date: "",
        guests: "",
        venue: "",
        portions: "",
        message: cake ? `I'd love to enquire about your ${cake.name} cake.` : "",
    });
    const [inspirationUrl, setInspirationUrl] = useState("");
    const [uploading, setUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const fileInputRef = useRef(null);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const uploadFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
            toast.error("Please upload an image or PDF.");
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            toast.error("File must be under 8 MB.");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/inquiry-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setInspirationUrl(r.data.url);
            toast.success("Inspiration uploaded");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const onFileChange = (e) => uploadFile(e.target.files?.[0]);

    const onDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        uploadFile(e.dataTransfer.files?.[0]);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            await api.post("/inquiries", {
                ...form,
                inspiration_url: inspirationUrl || null,
                cake_slug: cake?.slug,
                cake_name: cake?.name,
            });
            toast.success("Thank you! Your enquiry has been sent. We'll be in touch shortly.");
            setForm({ name: "", email: "", phone: "", event_date: "", guests: "", venue: "", portions: "", message: "" });
            setInspirationUrl("");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={onSubmit} className="space-y-4" data-testid="inquiry-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Name *" name="name" value={form.name} onChange={onChange} required testId="inquiry-name" />
                <Field label="Email *" name="email" type="email" value={form.email} onChange={onChange} required testId="inquiry-email" />
                <Field label="Contact Number *" name="phone" type="tel" value={form.phone} onChange={onChange} required testId="inquiry-phone" />
                <Field label="Date of Event *" name="event_date" type="date" value={form.event_date} onChange={onChange} required testId="inquiry-date" />
                <Field label="Number of Guests *" name="guests" type="number" min="1" value={form.guests} onChange={onChange} required testId="inquiry-guests" />
                <Field label="Name / Location of Venue *" name="venue" value={form.venue} onChange={onChange} required testId="inquiry-venue" />
            </div>

            <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">
                    Coffee or Dessert Portions? *
                </label>
                <div className="flex flex-wrap gap-2">
                    {["Coffee", "Dessert", "Both"].map((p) => (
                        <button
                            type="button"
                            key={p}
                            onClick={() => setForm({ ...form, portions: p })}
                            className={`px-4 py-2 text-xs tracking-[0.15em] uppercase border transition-colors ${
                                form.portions === p
                                    ? "bg-blush text-white border-blush"
                                    : "bg-white border-[#EAE3DE] text-ink hover:border-blush"
                            }`}
                            data-testid={`inquiry-portions-${p.toLowerCase()}`}
                        >
                            {p}
                        </button>
                    ))}
                </div>
                {/* Hidden input for form validation */}
                <input type="text" required value={form.portions} onChange={() => {}} className="sr-only" tabIndex={-1} aria-hidden />
            </div>

            <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">
                    Tell us about your cake
                </label>
                <textarea
                    name="message"
                    rows={compact ? 3 : 5}
                    value={form.message}
                    onChange={onChange}
                    className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink resize-none"
                    placeholder="Occasion, servings, flavours, design ideas…"
                    data-testid="inquiry-message"
                />
            </div>

            {/* Drag-and-drop file upload */}
            <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">
                    Upload a Design or Inspo Image (optional)
                </label>
                {inspirationUrl ? (
                    <div className="relative inline-block">
                        <img src={inspirationUrl} alt="Inspiration" className="w-32 h-32 object-cover border border-[#EAE3DE]" />
                        <button
                            type="button"
                            onClick={() => setInspirationUrl("")}
                            className="absolute -top-2 -right-2 bg-white border border-[#EAE3DE] rounded-full p-1 hover:bg-blush hover:text-white"
                            aria-label="Remove image"
                        >
                            <X size={12} />
                        </button>
                    </div>
                ) : (
                    <div
                        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                        onDragLeave={() => setDragActive(false)}
                        onDrop={onDrop}
                        onClick={() => fileInputRef.current?.click()}
                        className={`border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
                            dragActive ? "border-blush bg-blush-pale/40" : "border-[#EAE3DE] hover:border-blush"
                        }`}
                        data-testid="inquiry-dropzone"
                    >
                        {uploading ? (
                            <p className="text-sm text-ink-soft">Uploading…</p>
                        ) : (
                            <>
                                <Upload size={28} className="mx-auto mb-3 text-blush" />
                                <p className="text-sm text-ink mb-1">
                                    <span className="text-blush font-medium">Click to upload</span> or drag & drop
                                </p>
                                <p className="text-xs text-ink-soft">JPG, PNG, WEBP or PDF — up to 8 MB</p>
                            </>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*,application/pdf"
                            onChange={onFileChange}
                            className="hidden"
                            data-testid="inquiry-file-input"
                        />
                    </div>
                )}
            </div>

            <p className="text-[11px] text-ink-soft italic">* Required fields. We'll be in touch within 1–2 business days.</p>

            <button
                type="submit"
                disabled={submitting}
                className="btn-pink disabled:opacity-60"
                data-testid="inquiry-submit"
            >
                {submitting ? "Sending…" : "Send Enquiry"}
            </button>
        </form>
    );
}

function Field({ label, name, value, onChange, type = "text", required = false, min, testId }) {
    return (
        <div>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">{label}</label>
            <input
                type={type}
                name={name}
                required={required}
                min={min}
                value={value}
                onChange={onChange}
                className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink"
                data-testid={testId}
            />
        </div>
    );
}
